import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import FicheBijouTermine from './FicheBijouTermine';
import { isVendeuse } from '../../utils/typeCaisseLabels';

/**
 * Wrapper modal qui charge les données fiche OF via /atelier/fiche-fabrication/<ordre_id>
 * et délègue le rendu à FicheBijouTermine.
 *
 * Props:
 *   bijou : objet bijou (avec ordre_fabrication_id, image_url, etc.)
 *   onClose : fermer le sub-modal
 *   onRelancerSuccess : callback (newBijouId, newOfId) après relance réussie
 */
const FicheBijouTermineModal = ({ bijou, onClose, onRelancerSuccess }) => {
  const [ordreData, setOrdreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relanceLoading, setRelanceLoading] = useState(false);
  const [relanceError, setRelanceError] = useState(null);

  const ordreId = bijou?.ordre_fabrication_id;

  // §205 — Défense en profondeur : la fiche OF expose coût/marge/étapes/ouvriers/
  // matières. Interdite à la vendeuse (rôle 'boutique') quel que soit l'appelant.
  // Évalué sur user.role (jamais workspaceContext.type).
  const vendeuse = (() => {
    try { return isVendeuse(JSON.parse(localStorage.getItem('user') || '{}')?.role || ''); }
    catch { return false; }
  })();

  useEffect(() => {
    if (vendeuse) { setLoading(false); return; } // n'appelle même pas l'API
    if (!ordreId) {
      setError('Aucun OF associé à ce bijou');
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/atelier/fiche-fabrication/${ordreId}`);
        setOrdreData(res.data);
      } catch (e) {
        setError(e.response?.data?.error || e.message || 'Erreur chargement fiche OF');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ordreId, vendeuse]);

  // §205 — Refus net si vendeuse (ne rend ni la fiche ni les données chargées)
  if (vendeuse) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={onClose}>
        <div className="bg-white rounded-xl p-6 shadow-2xl max-w-md text-center" onClick={e => e.stopPropagation()}>
          <div className="text-gray-800 font-semibold mb-2">Accès non autorisé</div>
          <div className="text-gray-600 text-sm mb-4">La fiche de fabrication n'est pas accessible depuis un compte boutique.</div>
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-sm font-semibold">Fermer</button>
        </div>
      </div>
    );
  }

  const handleRelancer = async () => {
    if (!ordreId) return;
    if (!window.confirm('Relancer une production identique ? Un nouveau bijou + OF seront créés.')) return;
    try {
      setRelanceLoading(true);
      setRelanceError(null);
      const res = await api.post(`/atelier/ordres-fabrication/${ordreId}/relancer`, {
        quantite: 1
      });
      const data = res.data || {};
      // Compat backend : new_bijou_id / new_of_id (Q3) ou ordres_crees[]
      const newBijouId = data.new_bijou_id || null;
      const newOfId = data.new_of_id || null;
      if (onRelancerSuccess) onRelancerSuccess(newBijouId, newOfId, data);
      onClose();
    } catch (e) {
      setRelanceError(e.response?.data?.error || e.response?.data?.message || e.message || 'Erreur relance');
    } finally {
      setRelanceLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl px-6 py-4 shadow-2xl">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 text-sm">Chargement fiche OF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl p-6 shadow-2xl max-w-md">
          <div className="text-red-600 font-semibold mb-2">Erreur</div>
          <div className="text-gray-700 text-sm mb-4">{error}</div>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm">Fermer</button>
        </div>
      </div>
    );
  }

  // Wrapper Z-index supérieur (z-[60]) au modal parent (z-50)
  return (
    <div className="fixed inset-0 z-[60]">
      <FicheBijouTermine
        ordre={ordreData}
        onClose={onClose}
        imagePrincipale={bijou?.image_url}
        bijouLie={bijou}
        pierresOf={ordreData?.pierres_of || ordreData?.pierres}
        onRelancer={handleRelancer}
        relanceLoading={relanceLoading}
        relanceError={relanceError}
      />
    </div>
  );
};

export default FicheBijouTermineModal;
